/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Body_create_custom_team_role_v1_workspaces__slug__team_roles_custom_post } from '../models/Body_create_custom_team_role_v1_workspaces__slug__team_roles_custom_post';
import type { Body_update_custom_team_role_v1_workspaces__slug__team_roles__role_id__patch } from '../models/Body_update_custom_team_role_v1_workspaces__slug__team_roles__role_id__patch';
import type { TeamCreateRequest } from '../models/TeamCreateRequest';
import type { TeamDetail } from '../models/TeamDetail';
import type { TeamMemberBulkCreate } from '../models/TeamMemberBulkCreate';
import type { TeamMemberCreate } from '../models/TeamMemberCreate';
import type { TeamMemberDetail } from '../models/TeamMemberDetail';
import type { TeamMemberUpdateRequest } from '../models/TeamMemberUpdateRequest';
import type { TeamRole } from '../models/TeamRole';
import type { TeamUpdateRequest } from '../models/TeamUpdateRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class TeamService {
  constructor(public readonly httpRequest: BaseHttpRequest) {}

  /**
   * List Team
   * @param slug
   * @param memberId member_id가 속한 팀만 응답할지 여부
   * @returns TeamDetail Successful Response
   * @throws ApiError
   */
  public listTeamV1WorkspaceSlugTeamsGet(
    slug: string,
    memberId?: number | null
  ): CancelablePromise<Array<TeamDetail>> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspace/{slug}/teams',
      path: {
        slug: slug,
      },
      query: {
        member_id: memberId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Insufficient privileges`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Create Team
   * @param slug
   * @param requestBody
   * @returns TeamDetail Successful Response
   * @throws ApiError
   */
  public createTeamV1WorkspaceSlugTeamsPost(
    slug: string,
    requestBody: TeamCreateRequest
  ): CancelablePromise<TeamDetail> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspace/{slug}/teams',
      path: {
        slug: slug,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Unauthorized`,
        403: `Insufficient privileges`,
        409: `Team email already exists`,
        422: `Invalid Team name or email`,
      },
    });
  }

  /**
   * Get Team
   * @param slug
   * @param id
   * @returns TeamDetail Successful Response
   * @throws ApiError
   */
  public getTeamV1WorkspaceSlugTeamsIdGet(slug: string, id: number): CancelablePromise<TeamDetail> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspace/{slug}/teams/{id}',
      path: {
        slug: slug,
        id: id,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Team
   * @param slug
   * @param id
   * @param requestBody
   * @returns TeamDetail Successful Response
   * @throws ApiError
   */
  public updateTeamV1WorkspaceSlugTeamsIdPatch(
    slug: string,
    id: number,
    requestBody: TeamUpdateRequest
  ): CancelablePromise<TeamDetail> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/v1/workspace/{slug}/teams/{id}',
      path: {
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Unauthorized`,
        403: `Insufficient privileges`,
        404: `Team not found`,
        409: `Team email already exists`,
        422: `Invalid Team name or email`,
      },
    });
  }

  /**
   * Delete Team
   * @param slug
   * @param id
   * @returns void
   * @throws ApiError
   */
  public deleteTeamV1WorkspaceSlugTeamsIdDelete(slug: string, id: number): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/v1/workspace/{slug}/teams/{id}',
      path: {
        slug: slug,
        id: id,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Add Team Member
   * @param memberId
   * @param slug
   * @param id
   * @param requestBody
   * @returns TeamDetail Successful Response
   * @throws ApiError
   */
  public addTeamMemberV1WorkspaceSlugTeamsIdMembersMemberIdPost(
    memberId: number,
    slug: string,
    id: number,
    requestBody: TeamMemberCreate
  ): CancelablePromise<TeamDetail> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspace/{slug}/teams/{id}/members/{member_id}',
      path: {
        member_id: memberId,
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Team Members
   * @param slug
   * @param id
   * @returns TeamMemberDetail Successful Response
   * @throws ApiError
   */
  public getTeamMembersV1WorkspaceSlugTeamsIdTeamMembersGet(
    slug: string,
    id: number
  ): CancelablePromise<Array<TeamMemberDetail>> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspace/{slug}/teams/{id}/team-members',
      path: {
        slug: slug,
        id: id,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Add Team Members Bulk
   * @param slug
   * @param id
   * @param requestBody
   * @returns TeamDetail Successful Response
   * @throws ApiError
   */
  public addTeamMembersBulkV1WorkspaceSlugTeamsIdTeamMembersBulkPost(
    slug: string,
    id: number,
    requestBody: TeamMemberBulkCreate
  ): CancelablePromise<TeamDetail> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspace/{slug}/teams/{id}/team-members/bulk',
      path: {
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Team Member
   * @param teamMemberId
   * @param slug
   * @param id
   * @param requestBody
   * @returns TeamMemberDetail Successful Response
   * @throws ApiError
   */
  public updateTeamMemberV1WorkspaceSlugTeamsIdTeamMembersTeamMemberIdPatch(
    teamMemberId: number,
    slug: string,
    id: number,
    requestBody: TeamMemberUpdateRequest
  ): CancelablePromise<TeamMemberDetail> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/v1/workspace/{slug}/teams/{id}/team-members/{team_member_id}',
      path: {
        team_member_id: teamMemberId,
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Remove Team Member
   * @param teamMemberId
   * @param slug
   * @param id
   * @returns TeamDetail Successful Response
   * @throws ApiError
   */
  public removeTeamMemberV1WorkspaceSlugTeamsIdTeamMembersTeamMemberIdDelete(
    teamMemberId: number,
    slug: string,
    id: number
  ): CancelablePromise<TeamDetail> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/v1/workspace/{slug}/teams/{id}/team-members/{team_member_id}',
      path: {
        team_member_id: teamMemberId,
        slug: slug,
        id: id,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * List Team
   * @param slug
   * @param memberId member_id가 속한 팀만 응답할지 여부
   * @returns TeamDetail Successful Response
   * @throws ApiError
   */
  public listTeamV1WorkspacesSlugTeamsGet(
    slug: string,
    memberId?: number | null
  ): CancelablePromise<Array<TeamDetail>> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspaces/{slug}/teams',
      path: {
        slug: slug,
      },
      query: {
        member_id: memberId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Insufficient privileges`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Create Team
   * @param slug
   * @param requestBody
   * @returns TeamDetail Successful Response
   * @throws ApiError
   */
  public createTeamV1WorkspacesSlugTeamsPost(
    slug: string,
    requestBody: TeamCreateRequest
  ): CancelablePromise<TeamDetail> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspaces/{slug}/teams',
      path: {
        slug: slug,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Unauthorized`,
        403: `Insufficient privileges`,
        409: `Team email already exists`,
        422: `Invalid Team name or email`,
      },
    });
  }

  /**
   * Get Team
   * @param slug
   * @param id
   * @returns TeamDetail Successful Response
   * @throws ApiError
   */
  public getTeamV1WorkspacesSlugTeamsIdGet(
    slug: string,
    id: number
  ): CancelablePromise<TeamDetail> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspaces/{slug}/teams/{id}',
      path: {
        slug: slug,
        id: id,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Team
   * @param slug
   * @param id
   * @param requestBody
   * @returns TeamDetail Successful Response
   * @throws ApiError
   */
  public updateTeamV1WorkspacesSlugTeamsIdPatch(
    slug: string,
    id: number,
    requestBody: TeamUpdateRequest
  ): CancelablePromise<TeamDetail> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/v1/workspaces/{slug}/teams/{id}',
      path: {
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Unauthorized`,
        403: `Insufficient privileges`,
        404: `Team not found`,
        409: `Team email already exists`,
        422: `Invalid Team name or email`,
      },
    });
  }

  /**
   * Delete Team
   * @param slug
   * @param id
   * @returns void
   * @throws ApiError
   */
  public deleteTeamV1WorkspacesSlugTeamsIdDelete(
    slug: string,
    id: number
  ): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/v1/workspaces/{slug}/teams/{id}',
      path: {
        slug: slug,
        id: id,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Add Team Member
   * @param memberId
   * @param slug
   * @param id
   * @param requestBody
   * @returns TeamDetail Successful Response
   * @throws ApiError
   */
  public addTeamMemberV1WorkspacesSlugTeamsIdMembersMemberIdPost(
    memberId: number,
    slug: string,
    id: number,
    requestBody: TeamMemberCreate
  ): CancelablePromise<TeamDetail> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspaces/{slug}/teams/{id}/members/{member_id}',
      path: {
        member_id: memberId,
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Team Members
   * @param slug
   * @param id
   * @returns TeamMemberDetail Successful Response
   * @throws ApiError
   */
  public getTeamMembersV1WorkspacesSlugTeamsIdTeamMembersGet(
    slug: string,
    id: number
  ): CancelablePromise<Array<TeamMemberDetail>> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspaces/{slug}/teams/{id}/team-members',
      path: {
        slug: slug,
        id: id,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Add Team Members Bulk
   * @param slug
   * @param id
   * @param requestBody
   * @returns TeamDetail Successful Response
   * @throws ApiError
   */
  public addTeamMembersBulkV1WorkspacesSlugTeamsIdTeamMembersBulkPost(
    slug: string,
    id: number,
    requestBody: TeamMemberBulkCreate
  ): CancelablePromise<TeamDetail> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspaces/{slug}/teams/{id}/team-members/bulk',
      path: {
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Team Member
   * @param teamMemberId
   * @param slug
   * @param id
   * @param requestBody
   * @returns TeamMemberDetail Successful Response
   * @throws ApiError
   */
  public updateTeamMemberV1WorkspacesSlugTeamsIdTeamMembersTeamMemberIdPatch(
    teamMemberId: number,
    slug: string,
    id: number,
    requestBody: TeamMemberUpdateRequest
  ): CancelablePromise<TeamMemberDetail> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/v1/workspaces/{slug}/teams/{id}/team-members/{team_member_id}',
      path: {
        team_member_id: teamMemberId,
        slug: slug,
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Remove Team Member
   * @param teamMemberId
   * @param slug
   * @param id
   * @returns TeamDetail Successful Response
   * @throws ApiError
   */
  public removeTeamMemberV1WorkspacesSlugTeamsIdTeamMembersTeamMemberIdDelete(
    teamMemberId: number,
    slug: string,
    id: number
  ): CancelablePromise<TeamDetail> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/v1/workspaces/{slug}/teams/{id}/team-members/{team_member_id}',
      path: {
        team_member_id: teamMemberId,
        slug: slug,
        id: id,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Team Roles
   * 워크스페이스의 모든 팀 롤 조회 (팀 미팅 관리자 포함)
   * @param slug
   * @returns TeamRole Successful Response
   * @throws ApiError
   */
  public getTeamRolesV1WorkspacesSlugTeamRolesGet(
    slug: string
  ): CancelablePromise<Array<TeamRole>> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspaces/{slug}/team-roles',
      path: {
        slug: slug,
      },
      errors: {
        401: `Unauthorized`,
        403: `Insufficient privileges`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Create Custom Team Role
   * @param slug
   * @param requestBody
   * @returns TeamRole Successful Response
   * @throws ApiError
   */
  public createCustomTeamRoleV1WorkspacesSlugTeamRolesCustomPost(
    slug: string,
    requestBody: Body_create_custom_team_role_v1_workspaces__slug__team_roles_custom_post
  ): CancelablePromise<TeamRole> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspaces/{slug}/team-roles/custom',
      path: {
        slug: slug,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Invalid permissions`,
        401: `Unauthorized`,
        403: `Insufficient privileges`,
        409: `Role already exists`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Custom Team Role
   * @param roleId
   * @param slug
   * @param requestBody
   * @returns TeamRole Successful Response
   * @throws ApiError
   */
  public updateCustomTeamRoleV1WorkspacesSlugTeamRolesRoleIdPatch(
    roleId: number,
    slug: string,
    requestBody: Body_update_custom_team_role_v1_workspaces__slug__team_roles__role_id__patch
  ): CancelablePromise<TeamRole> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/v1/workspaces/{slug}/team-roles/{role_id}',
      path: {
        role_id: roleId,
        slug: slug,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Invalid permissions`,
        401: `Unauthorized`,
        403: `Insufficient privileges`,
        404: `Role not found`,
        409: `Role already exists`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Delete Custom Team Role
   * @param roleId
   * @param slug
   * @returns void
   * @throws ApiError
   */
  public deleteCustomTeamRoleV1WorkspacesSlugTeamRolesRoleIdDelete(
    roleId: number,
    slug: string
  ): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/v1/workspaces/{slug}/team-roles/{role_id}',
      path: {
        role_id: roleId,
        slug: slug,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }
}
